// Attach the Clerk session token to calls to the Singapore backend.
//
// The backend (backend/app/core/clerk_auth.py) verifies `Authorization: Bearer <token>` and
// replaces the X-User-Email / X-Clerk-User-Id headers with the verified identity, so every
// existing call keeps sending those headers unchanged. The token is only ever sent to the
// backend (same-origin /api, /v1, /eligibility, /export, or the backend's own host), never to
// third parties or to the US app under /us/app.

const BACKEND_PATH_PREFIXES = ["/api/", "/v1/", "/eligibility", "/export/"];
const BACKEND_HOSTS = new Set(["optra-backend.onrender.com"]);

function configuredBackendHosts() {
  const hosts = new Set(BACKEND_HOSTS);
  for (const value of [import.meta.env?.VITE_API_BASE_URL, import.meta.env?.VITE_API_FALLBACK_URL]) {
    try {
      if (value) hosts.add(new URL(value).host);
    } catch {
      // ignore malformed env values
    }
  }
  return hosts;
}

export function isBackendUrl(rawUrl) {
  if (typeof window === "undefined" || !rawUrl) return false;
  let url;
  try {
    url = new URL(String(rawUrl), window.location.href);
  } catch {
    return false;
  }
  if (url.origin === window.location.origin) {
    return BACKEND_PATH_PREFIXES.some((prefix) => url.pathname.startsWith(prefix));
  }
  if (configuredBackendHosts().has(url.host)) return true;
  // Local FastAPI during development.
  return (
    import.meta.env?.DEV === true &&
    (url.hostname === "localhost" || url.hostname === "127.0.0.1") &&
    url.port === "8000"
  );
}

export async function getClerkSessionToken() {
  try {
    const session = typeof window !== "undefined" ? window.Clerk?.session : null;
    if (!session?.getToken) return "";
    return (await session.getToken()) || "";
  } catch {
    return "";
  }
}

function hasAuthorization(headers) {
  if (!headers) return false;
  if (typeof Headers !== "undefined" && headers instanceof Headers) return headers.has("authorization");
  if (Array.isArray(headers)) return headers.some(([name]) => String(name).toLowerCase() === "authorization");
  return Object.keys(headers).some((name) => name.toLowerCase() === "authorization");
}

let fetchInstalled = false;

export function installBackendAuthFetch() {
  if (fetchInstalled || typeof window === "undefined" || typeof window.fetch !== "function") return;
  fetchInstalled = true;
  const originalFetch = window.fetch.bind(window);
  window.fetch = async function backendAuthFetch(input, init) {
    const url = typeof input === "string" || input instanceof URL ? String(input) : input?.url;
    if (!isBackendUrl(url)) return originalFetch(input, init);
    const existing = init?.headers ?? (typeof input === "object" && !(input instanceof URL) ? input.headers : undefined);
    if (hasAuthorization(existing)) return originalFetch(input, init);
    const token = await getClerkSessionToken();
    if (!token) return originalFetch(input, init);
    const headers = new Headers(existing || undefined);
    headers.set("Authorization", `Bearer ${token}`);
    return originalFetch(input, { ...(init || {}), headers });
  };
}

// Axios instances do not go through window.fetch; register this on each one.
export function attachBackendAuth(axiosInstance) {
  if (typeof axiosInstance?.interceptors?.request?.use !== "function") return axiosInstance;
  axiosInstance.interceptors.request.use(async (config) => {
    const base = config.baseURL || "";
    const target = /^https?:\/\//i.test(config.url || "") ? config.url : `${base.replace(/\/$/, "")}/${String(config.url || "").replace(/^\//, "")}`;
    if (!isBackendUrl(target)) return config;
    const current = config.headers?.Authorization || config.headers?.authorization;
    if (current) return config;
    const token = await getClerkSessionToken();
    if (token) {
      if (typeof config.headers?.set === "function") config.headers.set("Authorization", `Bearer ${token}`);
      else config.headers = { ...(config.headers || {}), Authorization: `Bearer ${token}` };
    }
    return config;
  });
  return axiosInstance;
}
